import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import BillingService from '../../services/BillingService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Checkbox,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import TaxService from '../../services/TaxService';
import PatientService from '../../services/PatientService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BillingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Patient list for dropdown
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  
  // Patient input mode state
  const [useRegisteredPatient, setUseRegisteredPatient] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    manualPatientName: '',
    date: null,
    invoiceNumber: '',
    description: '',
    status: 'Pending',
    paymentMethod: '',
    paymentDate: null,
    items: [
      { 
        id: Date.now(), 
        description: '', 
        quantity: 1, 
        unitPrice: 0, 
        discount: 0,
        subtotal: 0, 
        taxAmount: 0, 
        total: 0,
        taxEnabled: false,
        selectedTaxId: null
      }
    ],
    subtotal: 0,
    totalTaxAmount: 0,
    discount: 0,
    total: 0,
    notes: ''
  });
  
  // Tax configuration state
  const [taxConfigurations, setTaxConfigurations] = useState([]);
  const [loadingTaxConfig, setLoadingTaxConfig] = useState(true);
  
  useEffect(() => {
    // Fetch patients for dropdown
    const fetchPatients = async () => {
      try {
        setLoadingPatients(true);
        
        // Fetch active patients from the API
        const response = await PatientService.getActivePatients();
        setPatients(response.data);
        setLoadingPatients(false);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setLoadingPatients(false);
      }
    };
    
    fetchPatients();
    
    // Fetch all tax configurations
    const fetchTaxConfigurations = async () => {
      try {
        setLoadingTaxConfig(true);
        const allTaxConfigs = await TaxService.getAllTaxConfigurations();
        const activeTaxConfigs = (allTaxConfigs || []).filter(tc => {
          const isActive = tc?.isActive ?? tc?.IsActive ?? true;
          return Boolean(isActive);
        });
        setTaxConfigurations(activeTaxConfigs);
        setLoadingTaxConfig(false);
      } catch (error) {
        console.error('Error fetching tax configurations:', error);
        setLoadingTaxConfig(false);
      }
    };
    
    fetchTaxConfigurations();
    
    // Generate invoice number for new invoices
    if (!isEditMode) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      
      setFormData(prev => ({
        ...prev,
        invoiceNumber: `INV-${year}${month}${day}-${random}`,
        date: today
      }));
    }
    
    // If in edit mode, fetch billing data
    if (isEditMode) {
      const fetchBillingData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch real data from API
          const response = await BillingService.getInvoiceById(id);
          const invoice = response.data || response;
          
          console.log('Fetched invoice data:', invoice); // Debug log
          
          // Transform the data to match the form format
          const billingData = {
            id: invoice.invoiceId || invoice.InvoiceId,
            patientId: invoice.patientId || invoice.PatientId || '',
            patientName: invoice.patient ? 
              `${invoice.patient.firstName || invoice.patient.FirstName || ''} ${invoice.patient.lastName || invoice.patient.LastName || ''}`.trim() : 
              'Unknown Patient',
            date: invoice.invoiceDate || invoice.InvoiceDate ? new Date(invoice.invoiceDate || invoice.InvoiceDate) : new Date(),
            invoiceNumber: invoice.invoiceNumber || invoice.InvoiceNumber || `INV-${Date.now()}`,
            description: invoice.description || invoice.Description || 'Medical Services',
            status: invoice.status || invoice.Status || 'Pending',
            paymentMethod: invoice.paymentMethod || invoice.PaymentMethod || '',
            paymentDate: (invoice.paymentDate || invoice.PaymentDate) ? new Date(invoice.paymentDate || invoice.PaymentDate) : null,
            items: (invoice.invoiceItems || invoice.InvoiceItems || []).map(item => ({
              id: item.invoiceItemId || item.InvoiceItemId || Date.now(),
              description: item.notes || item.Notes || 
                          item.billingItem?.itemName || item.billingItem?.ItemName || 
                          item.BillingItem?.itemName || item.BillingItem?.ItemName || 
                          item.description || item.Description || 'Unknown Item',
              quantity: item.quantity || item.Quantity || 1,
              unitPrice: item.unitPrice || item.UnitPrice || 0,
              discount: item.discount || item.Discount || 0,
              subtotal: ((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)) - (item.discount || item.Discount || 0),
              taxAmount: item.taxAmount || item.TaxAmount || 0,
              total: item.totalAmount || item.TotalAmount || ((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)),
              taxEnabled: (item.taxAmount || item.TaxAmount || 0) > 0,
              selectedTaxId: (
                item.selectedTaxId ||
                item.SelectedTaxId ||
                (item.invoiceItemTaxes || item.InvoiceItemTaxes || [])[0]?.taxConfigId ||
                (item.invoiceItemTaxes || item.InvoiceItemTaxes || [])[0]?.TaxConfigId ||
                null
              )
            })),
            subtotal: invoice.subtotalAmount || invoice.SubtotalAmount || invoice.totalAmount || invoice.TotalAmount || 0,
            totalTaxAmount: invoice.totalTaxAmount || invoice.TotalTaxAmount || 0,
            discount: invoice.discountAmount || invoice.DiscountAmount || 0,
            total: invoice.totalAmount || invoice.TotalAmount || 0,
            notes: invoice.notes || invoice.Notes || ''
          };
          
          console.log('Transformed billing data:', billingData); // Debug log
          
          setFormData(billingData);
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching billing data:', error);
          setError('Failed to load billing data. Please try again later.');
          setLoading(false);
        }
      };
      
      fetchBillingData();
    }
  }, [id, isEditMode]);
  
  // Calculate totals whenever items or discount changes
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.subtotal, 0);
    const totalTaxAmount = formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
    const total = subtotal + totalTaxAmount - formData.discount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      totalTaxAmount,
      total
    }));
  }, [formData.items, formData.discount]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date
    });
  };
  
  const handlePaymentDateChange = (date) => {
    setFormData({
      ...formData,
      paymentDate: date
    });
  };
  
  const handlePatientChange = (e) => {
    const patientId = e.target.value;
    const selectedPatient = patients.find(patient => patient.patientId === patientId);
    
    setFormData({
      ...formData,
      patientId: patientId,
      patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : ''
    });
  };
  
  const handleStatusChange = (e) => {
    const status = e.target.value;
    let updatedData = {
      ...formData,
      status
    };
    
    // If status is changed to 'Paid' and no payment date is set, set it to today
    if (status === 'Paid') {
      if (!formData.paymentMethod) {
        updatedData.paymentMethod = 'Cash';
      }
      if (!formData.paymentDate) {
        updatedData.paymentDate = new Date();
      }
    } else {
      updatedData.paymentMethod = '';
      updatedData.paymentDate = null;
    }
    
    setFormData(updatedData);
  };
  
  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      subtotal: 0,
      taxAmount: 0,
      total: 0,
      taxEnabled: false,
      selectedTaxId: null
    };
    
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };
  
  const handleRemoveItem = (itemId) => {
    if (formData.items.length === 1) {
      return; // Don't remove the last item
    }
    
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.id !== itemId)
    });
  };
  
  const calculateItemTax = (subtotal, taxEnabled, selectedTaxId) => {
    if (!taxEnabled || !selectedTaxId || !subtotal || taxConfigurations.length === 0) return 0;
    
    const selectedTax = taxConfigurations.find(tax => (tax?.taxConfigId ?? tax?.TaxConfigId) === selectedTaxId);
    if (!selectedTax) return 0;
    
    // Calculate tax based on tax type
    const taxType = selectedTax?.taxType ?? selectedTax?.TaxType;
    const taxRate = selectedTax?.taxRate ?? selectedTax?.TaxRate ?? 0;
    const fixedAmount = selectedTax?.fixedAmount ?? selectedTax?.FixedAmount ?? 0;

    if (taxType === 'Fixed') {
      return fixedAmount || 0;
    }
    
    return Math.round((subtotal * (taxRate / 100)) * 100) / 100;
  };
  
  const handleItemChange = (itemId, field, value) => {
    const updatedItems = formData.items.map(item => {
      if (item.id === itemId) {
        const nextValue =
          field === 'selectedTaxId'
            ? (value === '' || value === null ? null : Number(value))
            : value;
        const updatedItem = { ...item, [field]: nextValue };
        if (field === 'taxEnabled' && nextValue === false) {
          updatedItem.selectedTaxId = null;
        }
        if (field === 'selectedTaxId' && nextValue === null) {
          updatedItem.taxEnabled = false;
        }
        
        // Recalculate subtotal, tax, and total if relevant fields change
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxEnabled' || field === 'selectedTaxId') {
          updatedItem.subtotal = (updatedItem.quantity * updatedItem.unitPrice) - (updatedItem.discount || 0);
          updatedItem.taxAmount = calculateItemTax(
            updatedItem.subtotal, 
            updatedItem.taxEnabled, 
            updatedItem.selectedTaxId
          );
          updatedItem.total = updatedItem.subtotal + updatedItem.taxAmount;
        }
        
        return updatedItem;
      }
      return item;
    });
    
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    // Validate form data
    const patientValid = useRegisteredPatient ? formData.patientId : formData.manualPatientName;
    if (!patientValid || !formData.date || !formData.invoiceNumber || !formData.description) {
      setError('Please fill in all required fields.');
      setSaving(false);
      return;
    }
    
    // Validate items
    const invalidItems = formData.items.some(item => !item.description || item.quantity <= 0);
    if (invalidItems) {
      setError('Please fill in all item details with valid quantities.');
      setSaving(false);
      return;
    }
    
    try {
      // Prepare invoice items for API
      const invoiceItems = formData.items.map(item => ({
        BillingItemId: 1, // Default billing item ID - this should be improved to use actual billing items
        Quantity: item.quantity,
        UnitPrice: item.unitPrice,
        Discount: item.discount || 0,
        Subtotal: item.subtotal,
        TaxAmount: item.taxAmount,
        TotalAmount: item.total,
        Notes: item.description, // Using description as notes for now
        SelectedTaxId: item.selectedTaxId
      }));

      // Prepare invoice data for API
      const invoiceData = {
        PatientId: useRegisteredPatient ? parseInt(formData.patientId) : null,
        InvoiceDate: formData.date.toISOString(),
        DueDate: new Date(formData.date.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from invoice date
        InvoiceNumber: formData.invoiceNumber,
        Description: formData.description,
        Status: formData.status,
        SubtotalAmount: formData.subtotal,
        TotalTaxAmount: formData.totalTaxAmount,
        DiscountAmount: formData.discount,
        TotalAmount: formData.total,
        Notes: formData.notes || null,
        InvoiceItems: invoiceItems
      };

      console.log(isEditMode ? 'Updating invoice with data:' : 'Creating invoice with data:', invoiceData);
      
      // Call the appropriate API method based on mode
      const paymentDetails = {
        paymentMethod: formData.paymentMethod || null,
        paymentDate: formData.paymentDate ? formData.paymentDate.toISOString() : null
      };
      if (isEditMode) {
        await BillingService.updateInvoice(id, { ...invoiceData, InvoiceId: parseInt(id) }, paymentDetails);
        toast.success('Invoice updated successfully!');
      } else {
        await BillingService.createInvoice(invoiceData, paymentDetails);
        toast.success('Invoice created successfully!');
      }
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/billing');
      }, 1500);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} billing record:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'save'} billing record: ${error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/billing"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Invoice' : 'New Invoice'}
          </Typography>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Invoice {isEditMode ? 'updated' : 'created'} successfully!
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Patient Information
                  </Typography>
                  <ToggleButtonGroup
                    value={useRegisteredPatient ? 'registered' : 'manual'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setUseRegisteredPatient(newValue === 'registered');
                        // Clear patient data when switching modes
                        setFormData(prev => ({
                          ...prev,
                          patientId: '',
                          patientName: '',
                          manualPatientName: ''
                        }));
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="registered">
                      Select Registered Patient
                    </ToggleButton>
                    <ToggleButton value="manual">
                      Enter Patient Name
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {useRegisteredPatient ? (
                  <FormControl fullWidth required>
                    <InputLabel id="patient-label">Patient</InputLabel>
                    <Select
                      labelId="patient-label"
                      id="patientId"
                      name="patientId"
                      value={formData.patientId}
                      onChange={handlePatientChange}
                      label="Patient"
                      disabled={saving || loadingPatients}
                    >
                      {patients.map((patient) => (
                        <MenuItem key={patient.patientId} value={patient.patientId}>
                          {`${patient.firstName} ${patient.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    required
                    label="Patient Name"
                    name="manualPatientName"
                    value={formData.manualPatientName}
                    onChange={handleInputChange}
                    disabled={saving}
                    placeholder="Enter patient name"
                  />
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Invoice Date *"
                    value={formData.date}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        required: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Invoice Number"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleStatusChange}
                    label="Status"
                    disabled={saving}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              {formData.status === 'Paid' && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="payment-method-label">Payment Method</InputLabel>
                      <Select
                        labelId="payment-method-label"
                        id="paymentMethod"
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                        fullWidth
                      >
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="E-Wallet">E-Wallet</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Payment Date"
                      type="date"
                      value={formData.paymentDate ? new Date(formData.paymentDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value ? new Date(e.target.value) : null })}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Invoice Items
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width="25%">Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="center">Tax Enabled</TableCell>
                        <TableCell width="15%">Tax Type</TableCell>
                        <TableCell align="right">Tax Amount</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.description}
                              onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                              disabled={saving}
                              placeholder="Item description"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)}
                              disabled={saving}
                              inputProps={{ min: 1, style: { textAlign: 'right' } }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                              disabled={saving}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01, style: { textAlign: 'right' } }
                              }}
                              sx={{ width: '120px' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount || 0}
                              onChange={(e) => handleItemChange(item.id, 'discount', parseFloat(e.target.value) || 0)}
                              disabled={saving}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01, style: { textAlign: 'right' } }
                              }}
                              sx={{ width: '120px' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.subtotal)}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={item.taxEnabled}
                              onChange={(e) => handleItemChange(item.id, 'taxEnabled', e.target.checked)}
                              disabled={saving || loadingTaxConfig}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth disabled={!item.taxEnabled || saving || loadingTaxConfig}>
                              <Select
                                value={item.selectedTaxId || ''}
                                onChange={(e) => handleItemChange(item.id, 'selectedTaxId', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>Select Tax</em>
                                </MenuItem>
                                {taxConfigurations.map((tax) => (
                                  <MenuItem key={tax?.taxConfigId ?? tax?.TaxConfigId} value={tax?.taxConfigId ?? tax?.TaxConfigId}>
                                    {(tax?.taxName ?? tax?.TaxName)} ({(tax?.taxType ?? tax?.TaxType) === 'Fixed' ? formatCurrency(tax?.fixedAmount ?? tax?.FixedAmount ?? 0) : `${tax?.taxRate ?? tax?.TaxRate}%`})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.taxAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.total)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={saving || formData.items.length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddItem}
                            disabled={saving}
                          >
                            Add Item
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} align="right">
                          <Typography variant="body2">Tax</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrency(formData.totalTaxAmount)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} align="right">
                          <Typography variant="body2">Discount</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={formData.discount}
                            onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                            disabled={saving}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              inputProps: { min: 0, step: 0.01, style: { textAlign: 'right' } }
                            }}
                            sx={{ width: '120px' }}
                          />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} align="right">
                          <Typography variant="h6">Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6">{formatCurrency(formData.total)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{ mr: 1 }}
                    component={RouterLink}
                    to="/billing"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Saving...
                      </>
                    ) : (
                      isEditMode ? 'Update Invoice' : 'Create Invoice'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingForm;
